/**
 * API Error Hierarchy
 * Follows industry standards for error categorization and handling
 */

export enum ErrorStatusCode {
  // Client errors
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  UnprocessableEntity = 422,
  TooManyRequests = 429,

  // Server errors
  InternalServerError = 500,
  BadGateway = 502,
  ServiceUnavailable = 503,
  GatewayTimeout = 504,
}

export type ApiErrorType =
  | 'network_error'
  | 'timeout_error'
  | 'validation_error'
  | 'authentication_error'
  | 'authorization_error'
  | 'not_found_error'
  | 'conflict_error'
  | 'server_error'
  | 'unknown_error';

export interface ApiErrorResponse {
  error?: string;
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
}

/**
 * Base API Error class
 */
export class ApiError extends Error {
  public readonly type: ApiErrorType;
  public readonly statusCode: number;
  public readonly response: ApiErrorResponse;
  public readonly isRetryable: boolean;
  public readonly isSuppressible: boolean;
  public readonly timestamp: Date;

  constructor(
    message: string,
    statusCode: number = 0,
    response: ApiErrorResponse = {},
    type: ApiErrorType = 'unknown_error',
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.response = response;
    this.type = type;
    this.timestamp = new Date();
    this.isRetryable = this.determineRetryability();
    this.isSuppressible = this.determineSuppressibility();

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  private determineRetryability(): boolean {
    // Don't retry client errors (4xx) except rate limiting
    if (this.statusCode >= 400 && this.statusCode < 500) {
      return this.statusCode === ErrorStatusCode.TooManyRequests;
    }

    // Retry server errors (5xx) and network errors
    return this.statusCode >= 500 || this.statusCode === 0;
  }

  private determineSuppressibility(): boolean {
    // Suppress non-critical errors
    return (
      (this.statusCode === ErrorStatusCode.NotFound && this.type === 'not_found_error') ||
      (this.statusCode === ErrorStatusCode.Forbidden && this.type === 'authorization_error')
    );
  }

  /**
   * Backward compatibility: alias for statusCode
   * Old code used error.status, new code uses error.statusCode
   */
  get status(): number {
    return this.statusCode;
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      statusCode: this.statusCode,
      response: this.response,
      timestamp: this.timestamp.toISOString(),
    };
  }
}

/**
 * Network-specific error (no response from server)
 */
export class NetworkError extends ApiError {
  constructor(message: string = 'Network request failed') {
    super(message, 0, {}, 'network_error');
    this.name = 'NetworkError';
  }
}

/**
 * Timeout-specific error
 */
export class TimeoutError extends ApiError {
  constructor(message: string = 'Request timeout') {
    super(message, 0, {}, 'timeout_error');
    this.name = 'TimeoutError';
  }
}

/**
 * Type guard to check if error is ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
